import { useState } from "react";
import { Save, Trash2 } from "lucide-react";
import { ChoiceField, PanelField, StatField, StepperField } from "@/components/signs/Fields";
import { LENGTH_PRESETS, feetLabel } from "@/lib/gangsheet";

/** Save this sheet under a name, or bring back one you saved before. Signed-in customers only. */
function SavedSheetsField({ sheet, theme, layouts, onSave, onOpen, onDelete }) {
    const [name, setName] = useState("");
    return (
        <PanelField label="Saved sheets" theme={theme} testid="field-saved" wide="w-[360px] max-w-[92vw]"
            value={layouts.length ? `${layouts.length} saved` : "None yet"}>
            {(close) => (
                <div className="p-1">
                    <div className="flex gap-1.5">
                        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Name this sheet" data-testid="layout-name"
                            className="min-w-0 flex-1 rounded-lg border border-zinc-300 bg-white px-2.5 py-1.5 text-sm text-zinc-900 outline-none"
                            onFocus={(e) => (e.target.style.borderColor = theme?.primary)} onBlur={(e) => (e.target.style.borderColor = "")} />
                        <button type="button" disabled={!sheet.items.length} onClick={() => { onSave(name); setName(""); }} data-testid="layout-save"
                            className="inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-bold disabled:opacity-40"
                            style={{ background: theme?.primary, color: theme?.on }}>
                            <Save size={13} /> Save
                        </button>
                    </div>
                    <div className="mt-2 max-h-48 space-y-1 overflow-y-auto">
                        {layouts.map((l) => (
                            <div key={l.id} className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-white px-2.5 py-1.5" data-testid={`layout-${l.id}`}>
                                <button type="button" onClick={() => { onOpen(l); close(); }} className="min-w-0 flex-1 truncate text-left text-sm text-zinc-800 hover:underline">
                                    {l.name} <span className="font-mono text-[10px] text-zinc-500">· {l.items.length} imgs</span>
                                </button>
                                <button type="button" onClick={() => onDelete(l.id)} className="text-zinc-400 hover:text-red-600" data-testid={`layout-delete-${l.id}`}><Trash2 size={13} /></button>
                            </div>
                        ))}
                        {!layouts.length && <p className="px-1 py-1 text-[11px] text-zinc-500">Nothing saved yet.</p>}
                    </div>
                </div>
            )}
        </PanelField>
    );
}

/** The row of white fields along the bottom of the gang-sheet builder. */
export default function GangDock({ sheet, theme, user, layouts, onSave, onOpen, onDelete }) {
    const lengthChoices = [
        { id: "auto", label: `Auto · ${feetLabel(sheet.lengthMode === "auto" ? sheet.lengthIn : Math.max(12, Math.ceil(sheet.contentLen * 2) / 2))}` },
        ...LENGTH_PRESETS.map((len) => ({ id: String(len), label: `${len / 12} ft` })),
    ];
    return (
        <div className="border-t border-zinc-200 bg-[#f7f7f8] px-4 py-3 sm:px-6" data-testid="gang-dock">
            <div className="flex gap-2 overflow-x-auto pb-1 lg:flex-wrap lg:justify-center lg:overflow-visible lg:pb-0">
                {sheet.films.length > 1 && (
                    <ChoiceField label="Film" theme={theme} testid="field-film" value={sheet.filmId} onChange={sheet.pickFilm}
                        choices={sheet.films.map((f) => ({ id: f.id, label: `${f.name} · ${f.printable_width_inches}" printable · $${Number(f.price_per_linear_foot).toFixed(2)}/ft`, note: f.turnaround || undefined }))}
                        help="This shop runs more than one roll. Pick the film and the sheet, the spacing and the price a foot follow it." />
                )}
                <ChoiceField label="Sheet length" theme={theme} testid="field-length" choices={lengthChoices} value={String(sheet.lengthMode)}
                    onChange={(id) => sheet.setLengthMode(id === "auto" ? "auto" : Number(id))}
                    help="Auto grows the sheet to fit your images. Pick a fixed length to fill it up." />
                <StepperField label="Identical sheets" theme={theme} testid="field-sheets" value={sheet.sheets} onChange={sheet.setSheets} />
                <StatField label="Sheet used" theme={theme} testid="field-coverage" value={`${sheet.cover}%`} pct={sheet.cover} />
                {user && <SavedSheetsField sheet={sheet} theme={theme} layouts={layouts} onSave={onSave} onOpen={onOpen} onDelete={onDelete} />}
            </div>
        </div>
    );
}
